import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  ArrowLeft, Clock, Play, Shield, Trash2, MessageSquare,
  Bell, FileText, DollarSign, AlertTriangle, ClipboardList
} from 'lucide-react';

const API = `${window.location.origin}/api`;

const MODULE_ICONS = {
  disputes: AlertTriangle,
  warranty_claims: Shield,
  notifications: Bell,
  audit_logs: ClipboardList,
  cleanup_logs: Trash2,
  transactions: DollarSign,
  chat_messages: MessageSquare,
  admin_alerts: FileText
};

export default function DataRetention() {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/data-retention`);
      setPolicies(res.data.policies || []);
    } catch (e) {
      console.error('Failed to fetch policies:', e);
      toast.error('Failed to load retention policies');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const updatePolicy = async (module, field, value) => {
    try {
      const params = new URLSearchParams();
      params.append(field, value);
      const res = await axios.put(`${API}/admin/data-retention/${module}?${params}`);
      setPolicies(res.data.policies || []);
      toast.success(`${module.replace(/_/g, ' ')} policy updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update policy');
    }
  };

  const runCleanupNow = async () => {
    setIsRunning(true);
    try {
      const res = await axios.post(`${API}/admin/data-retention/run`);
      setLastResult(res.data.stats);
      toast.success('Data retention cleanup completed!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]" data-testid="data-retention-title">Data Retention</h1>
              <p className="text-slate-500">Configure automatic cleanup of old records. Runs daily at 2:00 AM.</p>
            </div>
          </div>
          <Button
            onClick={runCleanupNow}
            disabled={isRunning}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="run-cleanup-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {isRunning ? 'Running...' : 'Run Now'}
          </Button>
        </div>

        {lastResult && (
          <div className="bg-white rounded-xl p-5 shadow-sm mb-6 border border-green-200" data-testid="cleanup-result">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-green-600" />
              Last Cleanup Result
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(lastResult).map(([module, result]) => (
                <div key={module} className="bg-stone-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase font-medium">{module.replace(/_/g, ' ')}</p>
                  {result.skipped ? (
                    <p className="text-sm text-stone-400 mt-1">Skipped (disabled)</p>
                  ) : result.error ? (
                    <p className="text-sm text-red-500 mt-1">Error</p>
                  ) : (
                    <p className="text-lg font-bold text-slate-900 mt-1">{result.deleted} <span className="text-xs text-slate-400 font-normal">deleted</span></p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {policies.map((policy) => {
              const Icon = MODULE_ICONS[policy.module] || FileText;
              return (
                <div key={policy.module} className="bg-white rounded-xl p-5 shadow-sm" data-testid={`policy-${policy.module}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${policy.enabled ? 'bg-slate-900 text-white' : 'bg-stone-100 text-stone-400'}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-bold text-slate-900">{policy.label}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${policy.enabled ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-400'}`}>
                            {policy.enabled ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">{policy.description}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-600">Delete after</span>
                            <select
                              value={policy.retention_months}
                              onChange={(e) => updatePolicy(policy.module, 'retention_months', parseInt(e.target.value))}
                              className="h-9 border border-stone-200 rounded-lg px-3 text-sm font-medium text-slate-900"
                              disabled={!policy.enabled}
                              data-testid={`retention-months-${policy.module}`}
                            >
                              {[1, 2, 3, 6, 9, 12, 18, 24].map(m => (
                                <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>
                              ))}
                            </select>
                          </div>
                          {policy.filter_field && policy.filter_values?.length > 0 && (
                            <span className="text-xs text-slate-400">
                              Only: {policy.filter_values.map(v => String(v)).join(', ')} {policy.filter_field}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={policy.enabled}
                      onCheckedChange={(checked) => updatePolicy(policy.module, 'enabled', checked)}
                      data-testid={`toggle-${policy.module}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 bg-amber-50 rounded-xl p-5 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-900">Important Notes</h4>
              <ul className="text-sm text-amber-800 mt-2 space-y-1 list-disc list-inside">
                <li>Deleted records cannot be recovered. Make sure backups are configured before enabling.</li>
                <li>Transactions are disabled by default and set to 12 months to preserve accounting records.</li>
                <li>The cleanup job runs automatically every day at 2:00 AM server time.</li>
                <li>Use "Run Now" to execute an immediate cleanup based on current settings.</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
